import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { LoadingService } from '@shared/components/loading-dialog/loading-dialog.component';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import moment from 'moment';
import { DeletePricingCountryBundlePriceAction } from '../../../store/pricing-country.actions';
import {
  CountryBundlePrice,
  CountryBundleRow,
  CountryPriceType,
} from '../../../types/pricing.types';

/**
 * Deletes a bundle's latest WSP or EUP for a country. Only prices that have
 * not taken effect yet can be deleted (API rule), so the select offers just
 * those types.
 */
@Component({
  selector: 'app-delete-country-price-dialog',
  imports: [
    TranslatePipe,
    SelectComponent,
    SelectOptionComponent,
    DatePipe,
    DecimalPipe,
  ],
  templateUrl: './delete-country-price-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteCountryPriceDialogComponent {
  public countryId = signal<string>(null);
  public row = signal<CountryBundleRow>(null);
  public currency = signal<string>(null);

  protected modalRef = inject(NgbActiveModal);
  private store = inject(Store);
  private loadingService = inject(LoadingService);
  private translateService = inject(TranslateService);

  protected selectedType = signal<CountryPriceType>(null);

  /** Types whose current price is still in the future (deletable). */
  protected deletableTypes = computed<CountryPriceType[]>(() =>
    [CountryPriceType.WSP, CountryPriceType.EUP].filter((type) =>
      DeleteCountryPriceDialogComponent.isDeletable(this.priceOf(type))
    )
  );

  protected selectedPrice = computed(() =>
    this.selectedType() ? this.priceOf(this.selectedType()) : null
  );

  constructor() {
    effect(() => {
      const types = this.deletableTypes();

      if (!types.includes(this.selectedType())) {
        this.selectedType.set(types[0] ?? null);
      }
    });
  }

  static isDeletable(price: CountryBundlePrice | null): boolean {
    return (
      !!price?.pivot?.price && moment(price.pivot.validFrom).isAfter(moment())
    );
  }

  protected priceOf(type: CountryPriceType): CountryBundlePrice | null {
    return type === CountryPriceType.WSP ? this.row()?.wsp : this.row()?.eup;
  }

  protected delete() {
    const price = this.selectedPrice();

    if (!price) {
      return;
    }

    this.loadingService.wait({
      message: this.translateService.instant(
        'pricing.country.details.loading.deletingPrice'
      ),
      observableToFinish$: this.store.dispatch(
        new DeletePricingCountryBundlePriceAction(
          this.countryId(),
          price.pivot.id
        )
      ),
    });

    this.modalRef.close();
  }
}
